<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KnowledgebasesAPI from 'dashboard/api/knowledgebases';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const dialogRef = ref(null);
const galleries = ref([]);
const galleriesLoading = ref(false);
const selectedGalleryId = ref(null);
const images = ref([]);
const imagesLoading = ref(false);
const searchQuery = ref('');

const filteredImages = computed(() => {
  if (!searchQuery.value) return images.value;
  const q = searchQuery.value.toLowerCase();
  return images.value.filter(img => img.name.toLowerCase().includes(q));
});

const selectedImageName = computed(() => {
  if (!props.modelValue) return '';
  const parts = props.modelValue.split('/');
  return decodeURIComponent(parts[parts.length - 1]);
});

async function loadGalleries() {
  galleriesLoading.value = true;
  try {
    const response = await KnowledgebasesAPI.get();
    galleries.value = (response.data || []).filter(kb => kb.type === 'gallery');
  } catch {
    // ignore
  } finally {
    galleriesLoading.value = false;
  }
}

async function loadImages(galleryId) {
  if (!galleryId) {
    images.value = [];
    return;
  }
  imagesLoading.value = true;
  try {
    const response = await KnowledgebasesAPI.getRecords(galleryId, {
      per_page: 100,
    });
    images.value = (response.data.records || [])
      .filter(r => r.file?.url)
      .map(r => ({ id: r.id, url: r.file.url, name: r.name }));
  } catch {
    images.value = [];
  } finally {
    imagesLoading.value = false;
  }
}

watch(selectedGalleryId, id => loadImages(id));

watch(galleries, list => {
  if (list.length && !selectedGalleryId.value) {
    selectedGalleryId.value = list[0].id;
  }
});

function openPicker() {
  dialogRef.value?.open();
}

function selectImage(url) {
  emit('update:modelValue', url);
  dialogRef.value?.close();
}

function clearImage() {
  emit('update:modelValue', '');
}

defineExpose({ openPicker, clearImage });

onMounted(loadGalleries);
</script>

<template>
  <!-- Card layout when label is provided -->
  <div v-if="label" class="flex items-start gap-4">
    <div
      class="w-32 h-24 rounded-lg overflow-hidden bg-n-slate-3 flex-shrink-0 border border-n-slate-6"
    >
      <img
        v-if="modelValue"
        :src="modelValue"
        alt=""
        class="w-full h-full object-cover"
      />
      <div v-else class="flex items-center justify-center w-full h-full">
        <span class="i-lucide-image w-6 h-6 text-n-slate-8" />
      </div>
    </div>
    <div class="flex flex-col gap-1">
      <p class="font-semibold text-sm text-n-slate-12">{{ label }}</p>
      <p v-if="description" class="text-sm text-n-slate-10">
        {{ description }}
      </p>
      <button
        type="button"
        class="mt-1 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-n-slate-2 hover:bg-n-slate-3 transition-colors self-start"
        @click="openPicker"
      >
        <span class="i-lucide-folder-search w-4 h-4 text-woot-600" />
        <span class="font-medium text-woot-600">{{
          t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.GALLERY_PICKER_BROWSE')
        }}</span>
      </button>
    </div>
  </div>

  <!-- Default compact layout -->
  <div v-else class="flex items-center gap-2">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-slate-6 text-n-slate-12 hover:bg-n-slate-3 transition-colors max-w-[240px]"
      @click="openPicker"
    >
      <span class="i-lucide-image w-4 h-4 text-n-slate-11 flex-shrink-0" />
      <span class="truncate">
        {{
          modelValue
            ? selectedImageName
            : t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.GALLERY_PICKER_BUTTON')
        }}
      </span>
    </button>
    <button
      v-if="modelValue"
      type="button"
      class="flex-shrink-0 text-n-slate-10 hover:text-ruby-600 transition-colors"
      :aria-label="
        t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BACKGROUND_IMAGE_CLEAR')
      "
      @click="clearImage"
    >
      <span class="i-lucide-x w-3.5 h-3.5" />
    </button>
  </div>

  <Dialog
    ref="dialogRef"
    :title="t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.GALLERY_PICKER_TITLE')"
    width="3xl"
    position="top"
    :show-confirm-button="false"
  >
    <!-- Two-pane Finder-style layout -->
    <div class="flex h-[40rem] -mx-2 -mb-6 overflow-hidden rounded-b-xl">
      <!-- Left: gallery sidebar -->
      <div
        class="flex flex-col w-56 flex-shrink-0 border-r border-n-slate-6 overflow-y-auto py-1"
      >
        <div
          v-if="galleriesLoading"
          class="flex items-center justify-center h-full text-xs text-n-slate-10"
        >
          {{
            t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BACKGROUND_IMAGE_LOADING')
          }}
        </div>
        <button
          v-for="gallery in galleries"
          :key="gallery.id"
          type="button"
          class="flex items-center gap-2 w-full px-3 py-2 text-left text-sm transition-colors truncate"
          :class="
            selectedGalleryId === gallery.id
              ? 'bg-woot-500/10 text-woot-600 font-medium'
              : 'text-n-slate-12 hover:bg-n-slate-3'
          "
          @click="selectedGalleryId = gallery.id"
        >
          <span class="i-lucide-images w-3.5 h-3.5 flex-shrink-0" />
          <span class="truncate">{{ gallery.name }}</span>
        </button>
        <p
          v-if="!galleriesLoading && !galleries.length"
          class="text-xs text-n-slate-10 px-3 py-2"
        >
          {{ t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.GALLERY_EMPTY') }}
        </p>
      </div>

      <!-- Right: search + image grid -->
      <div class="flex flex-col flex-1 min-w-0 overflow-hidden">
        <div class="px-4 pt-3 pb-2 flex-shrink-0">
          <Input
            v-model="searchQuery"
            size="sm"
            :placeholder="
              t(
                'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.GALLERY_SEARCH_PLACEHOLDER'
              )
            "
          />
        </div>
        <div class="flex-1 overflow-y-auto px-4 pb-4">
          <div
            v-if="imagesLoading"
            class="flex items-center justify-center h-full text-sm text-n-slate-10"
          >
            {{
              t(
                'INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.BACKGROUND_IMAGE_LOADING'
              )
            }}
          </div>
          <div v-else-if="filteredImages.length" class="grid grid-cols-3 gap-3">
            <button
              v-for="img in filteredImages"
              :key="img.id"
              type="button"
              class="relative overflow-hidden rounded-md aspect-square border-2 transition-all"
              :class="
                modelValue === img.url
                  ? 'border-woot-500 ring-1 ring-woot-500'
                  : 'border-n-slate-6 hover:border-n-slate-8'
              "
              :title="img.name"
              @click="selectImage(img.url)"
            >
              <img
                :src="img.url"
                :alt="img.name"
                class="w-full h-full object-cover"
              />
            </button>
          </div>
          <div
            v-else-if="selectedGalleryId && !imagesLoading"
            class="flex items-center justify-center h-full text-sm text-n-slate-10"
          >
            {{ t('INBOX_MGMT.WIDGET_BUILDER.THEME.FIELDS.GALLERY_EMPTY') }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end w-full">
        <button
          type="button"
          class="px-4 py-2 text-sm rounded-lg border border-n-slate-6 text-n-slate-12 hover:bg-n-slate-3 transition-colors"
          @click="dialogRef?.close()"
        >
          {{ t('DIALOG.BUTTONS.CANCEL') }}
        </button>
      </div>
    </template>
  </Dialog>
</template>
